import { z } from 'zod';

import { AGENT_NOTE_RANGE_PRESETS } from '@apps/web/features/admin_agent_notes/model/range_presets';
import { type components } from '@libraries/typescript/api_client';

type AgentNoteStatus = components['schemas']['AgentNoteStatus'];
type AgentNoteSource = components['schemas']['AgentNoteSource'];
type AgentNoteDomain = components['schemas']['AgentNoteDomain'];

export const AGENT_NOTE_TABS = ['notes', 'dashboard'] as const;
export type AgentNotesTab = (typeof AGENT_NOTE_TABS)[number];

export const AGENT_NOTE_STATUSES = [
    'pending',
    'classified',
    'processed',
    'partially_processed',
    'fallback',
    'failed',
] as const satisfies ReadonlyArray<AgentNoteStatus>;

export const AGENT_NOTE_SOURCES = [
    'sms',
    'voice',
    'voice_post_call',
    'sidebar',
    'slack',
    'outreach',
    'automation',
    'ui',
    'sync',
    'feedback',
] as const satisfies ReadonlyArray<AgentNoteSource>;

export const AGENT_NOTE_DOMAINS = [
    'availability',
    'memory',
    'client_caregiver_preference',
    'attribute',
    'playbook_rule',
    'recommendation_default',
    'agency_context',
    'outreach_filter',
] as const satisfies ReadonlyArray<AgentNoteDomain>;

export const PER_PAGE = 50;

export const agentNotesSearchSchema = z.object({
    tab: z.enum(AGENT_NOTE_TABS).catch('notes'),
    // Relative date range; kept relative in the URL so pinned views stay
    // "the last N days". Fixed created_after/created_before take precedence.
    range: z.enum(AGENT_NOTE_RANGE_PRESETS).optional().catch(undefined),
    page: z.coerce.number().int().min(1).catch(1),
    status: z.enum(AGENT_NOTE_STATUSES).optional().catch(undefined),
    source: z.enum(AGENT_NOTE_SOURCES).optional().catch(undefined),
    domain: z.enum(AGENT_NOTE_DOMAINS).optional().catch(undefined),
    organization_id: z.string().optional(),
    created_after: z.string().optional(),
    created_before: z.string().optional(),
    search: z.string().optional(),
    note: z.string().optional(),
});

export type AgentNotesSearch = z.infer<typeof agentNotesSearchSchema>;
